import React, { useState } from "react";
import SectionCoachmark from "@/components/SectionCoachmark";
import AssetPreviewModal from "@/components/AssetPreviewModal";
import MaterialPreviewModal from "@/components/MaterialPreviewModal";
import StudioVisivoCard from "@/components/StudioVisivoCard";
import ListingAnalysisModal from "@/components/ListingAnalysisModal";
import PortalPackModal from "@/components/PortalPackModal";
import { authedFileUrl, downloadFile } from "@/components/propertyDetail/helpers";
import { api, API } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { isIOSDevice } from "@/lib/staticExport";
import Cookies from "js-cookie";
import { toast } from "sonner";
import {
  DownloadSimple, Archive, FilePdf, FileText, House, Eye, QrCode,
  ShareNetwork, WhatsappLogo, Copy, Export, Image as ImageIcon,
  Package, HandCoins,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";

export default function MaterialiTab() {
  const {
    id, property, photos, realPhotos, floorPlans, listing, setTab, tab,
    onDownloadZip, onExportXml,
    onOpenShare, copyPublicLink, downloadQr, publicListingUrl, publicShareUrl,
  } = usePropertyDetail();
  // iter 272 round 2: preview modal universale (PDF/IMG/TEXT)
  const [preview, setPreview] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  // iter v28 r348 — Modal "Cosa pubblicare" prima di scaricare il pacchetto
  // portale (Idealista/Immobiliare). Permette di:
  //  - vedere quante foto sono escluse dai portali
  //  - scegliere la versione della pianta (arredata · originale · entrambe · nessuna)
  const [portalPack, setPortalPack] = useState(null);
  // iter 296: modal "needs setup" — apre quando il materiale richiede
  // un prerequisito (es. Volantino → pagina pubblica) invece di redirect
  // silenzioso. Mostra anteprima + pulsante "Configura prima".
  const [setupGate, setSetupGate] = useState(null);
  // iter v28 r377 — Dopo il download del Pacchetto cliente, mostriamo un
  // mini-modal "Ora mandalo al venditore" con il link wa.me/?text=…
  // pre-compilato (saluti formali, titolo immobile, link vetrina). Solo
  // il "client" pack: il kit completo è uso interno, niente prompt.
  const [clientPackReady, setClientPackReady] = useState(null);  // { propertyTitle, vetrinaUrl }
  // iter v28 r377 (future #1) — Preview modal del Pacchetto cliente.
  // Compare PRIMA del download: l'agente vede la mini-galleria delle
  // foto migliorate, la caption che andrà nel zip e il link vetrina.
  // Poi conferma e parte il download (che a sua volta apre il modal WA).
  const [clientPackPreview, setClientPackPreview] = useState(null);  // payload da /kit/preview

  // iter v33 r4 — User M1474+M1476: «Hai eliminato il pacchetto completo
  // che promette cose non vere?». Sì lato sopralluogo, ma qui in Materiali
  // i bottoni di download apparivano sempre cliccabili anche con foto in
  // lavorazione. Calcolo `kitReady` = tutte le foto reali hanno
  // `enhanced_path`. Se non pronto: i due bottoni ZIP diventano grigi
  // con sub-label "Disponibile fra ~X min · sto preparando foto" e mostrano
  // un counter X/Y. Niente più click a vuoto.
  const realPhotosOnly = (realPhotos || []).filter((p) => !p.is_floor_plan);
  const enhancedReady = realPhotosOnly.filter((p) => !!p.enhanced_path).length;
  const totalPhotosForKit = realPhotosOnly.length;
  const kitReady = totalPhotosForKit > 0 && enhancedReady >= totalPhotosForKit;
  const kitWaitingLabel = totalPhotosForKit > 0
    ? `Disponibile fra ~${Math.max(1, totalPhotosForKit - enhancedReady)} min · ${enhancedReady}/${totalPhotosForKit} foto pronte`
    : "Aggiungi prima qualche foto all'annuncio";
  return (
      <div className="mt-8" data-testid="tab-file">
        {/* iter v33 r9 · Coachmark uniforme che sostituisce il vecchio
            DismissibleIntro. Ora è "Promuovi", dopo la pubblicazione. */}
        <SectionCoachmark
          id="property-materiali"
          title="Materiali pronti da inviare"
          steps={[
            {
              icon: <Package size={15} weight="bold" />,
              title: "Pacchetto portali",
              body: "Foto migliorate + pianta + testi ottimizzati, pronti per Immobiliare o Idealista.",
            },
            {
              icon: <DownloadSimple size={15} weight="bold" />,
              title: "Materiali singoli",
              body: "Scarichi foto, descrizione, prima/dopo, QR code — quello che ti serve al momento.",
            },
            {
              icon: <HandCoins size={15} weight="bold" />,
              title: "Pacchetto per il proprietario",
              body: "Un ZIP curato da consegnare al venditore, con report visuale del lavoro fatto.",
            },
          ]}
        />

        {(() => {
          const enhancedCount = realPhotos.filter((p) => p.enhanced_path).length;
          // iter v28 r348 — Conteggi pacchetto portale: foto escluse + pianta
          const realPhotosNoPlan = realPhotos.filter((p) => !p.is_floor_plan);
          const portalExcludedCount = realPhotosNoPlan.filter((p) => p.portal_excluded).length;
          const portalIncludedCount = realPhotosNoPlan.filter((p) => p.enhanced_path && !p.portal_excluded).length;
          const hasPlan = !!(floorPlans[0]?.furnished_plan_path);
          const hasOriginalPlan = !!(floorPlans[0]?.original_path);
          const hasDescription = !!property?.ai_description;
          const hasListing = !!listing?.exists;
          // iter 313 r9 — `publicUrl` per display all'agente (resta `/listing/X`,
          // più pulito), `shareUrl` per condividere (`/api/share/X` → preview OG su WhatsApp).
          const publicUrl = listing?.public_id ? `${window.location.origin}/listing/${listing.public_id}` : "";
          const shareUrl = listing?.public_id ? `${window.location.origin}/api/share/${listing.public_id}` : "";

          // iter 261: nuova prop `needsSetup` — invece di disabilitare
          // del tutto il tile quando manca il materiale, mostriamo un
          // badge giallo "⊕ Da creare" e il tile resta cliccabile per
          // portare l'utente lì dove crearlo.
          const Tile = ({ icon: Icn, label, sub, action, disabled, testId, primary, needsSetup }) => (
            <button
              onClick={action}
              disabled={disabled}
              className={`relative text-left p-4 rounded-xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                primary
                  ? "bg-[#1C1C1A] border-[#1C1C1A] text-white hover:bg-black"
                  : needsSetup
                    ? "bg-[#FFF8E7] border-[#E8D9A8] hover:border-[#C49A2A] text-[#1C1C1A]"
                    : "bg-white border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A]"
              }`}
              data-testid={testId}
            >
              {needsSetup && (
                <span
                  className="absolute top-2.5 right-2.5 text-[9px] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5 rounded bg-[#C49A2A] text-white"
                  data-testid={`${testId}-needs-setup`}
                >
                  Da creare
                </span>
              )}
              <div className="flex items-start gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-lg inline-flex items-center justify-center ${
                  primary ? "bg-white/10 text-white" : needsSetup ? "bg-[#F5E9B8] text-[#7A5C00]" : "bg-[#F1F4ED] text-[#7C8B6E]"
                }`}>
                  <Icn size={18} weight="fill" />
                </div>
                <div className="flex-1 min-w-0 pr-12">
                  <div className="text-[13.5px] font-semibold leading-tight">{label}</div>
                  <div className={`text-[11.5px] mt-1 leading-snug ${primary ? "text-white/75" : "text-[#6B6A65]"}`}>
                    {sub}
                  </div>
                </div>
              </div>
            </button>
          );

          // iter v28 r376 — User M1286 (IMG_0999): Materiali era confuso.
          // Ristrutturato in 3 macro-sezioni con titolo + sottotitolo per
          // far capire all'agente CHE COSA serve per CHE COSA:
          //   1. Da mandare al cliente  → pacchetto cliente + link + QR + WA
          //   2. Da pubblicare sui portali → pacchetto Immobiliare.it + feed XML
          //      (Idealista pubblica via feed automatico, niente pacchetto)
          //   3. Materiali singoli → kit completo + foto/pianta/PDF/testo
          // "Analizza annuncio" rimosso (utente diceva «non ho capito cos'è»).
          const Section = ({ eyebrow, title, sub, children }) => (
            <div className="mb-8" data-testid={`materiali-section-${eyebrow.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">{eyebrow}</div>
                <h4 className="font-editorial text-[19px] sm:text-[22px] mt-1 leading-tight">{title}</h4>
                {sub && <p className="text-[12.5px] text-[#6B6A65] mt-1 max-w-xl leading-snug">{sub}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl">
                {children}
              </div>
            </div>
          );

          const downloadKit = async (audience = "full") => {
            try {
              const token = Cookies.get("vista_jwt");
              toast.info(audience === "client" ? "Preparo il pacchetto cliente…" : "Preparo il kit completo…");
              const r = await fetch(`${API}/properties/${id}/kit/download?audience=${audience}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              // iter v33 r3 — Gestisco 425 (Too Early) con toast chiaro:
              // il backend rifiuta lo ZIP se le foto non sono tutte pronte
              // (no più cartelle vuote da scaricare).
              if (r.status === 425) {
                let detail = "Le foto sono ancora in lavorazione. Aspetta che Pronto finisca.";
                try {
                  const j = await r.clone().json();
                  if (j?.detail) detail = j.detail;
                } catch { /* noop */ }
                toast.warning("Pacchetto non ancora pronto", { description: detail, duration: 7000 });
                return;
              }
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              const blob = await r.blob();
              const filename = audience === "client"
                ? `pronto-pacchetto-cliente-${id.slice(0, 8)}.zip`
                : `pronto-pacchetto-${id.slice(0, 8)}.zip`;

              // iter v29 r168 — Native iOS: salva in Files + apri share
              // sheet così l'agente può girare lo ZIP via WhatsApp, Mail,
              // AirDrop, Drive con un solo tap. Su web/Android: download
              // tradizionale via <a download>.
              try {
                const { isNative, nativeShare } = await import("@/lib/native");
                if (isNative) {
                  const { Filesystem, Directory } = await import("@capacitor/filesystem");
                  const reader = new FileReader();
                  const dataUrl = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  });
                  const base64 = String(dataUrl).split(",")[1];
                  await Filesystem.writeFile({
                    path: filename,
                    data: base64,
                    directory: Directory.Cache,
                    recursive: true,
                  });
                  const uri = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
                  await nativeShare({
                    title: audience === "client" ? "Pacchetto cliente" : "Materiali Pronto",
                    text: "ZIP pronto",
                    url: uri.uri,
                    files: [uri.uri],
                  });
                } else {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1500);
                }
              } catch (_) {
                // Fallback se native share fallisce → download web standard
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1500);
              }
              toast.success(audience === "client" ? "Pacchetto cliente pronto" : "Kit completo scaricato");
              // iter v28 r377 — Dopo il pacchetto cliente, chiediamo se vuole
              // mandarlo subito al venditore via WhatsApp con messaggio
              // pre-compilato (saluti + titolo + link vetrina).
              if (audience === "client") {
                setClientPackReady({
                  propertyTitle: property?.title || "il Suo immobile",
                  vetrinaUrl: shareUrl,
                });
              }
            } catch (e) {
              toast.error("Download fallito: " + (e.message || ""));
            }
          };

          return (
            <>
              {/* ═══ 1. Per i portali (priorità: è la cosa più importante per pubblicare) ═══
                  iter v28 r378 — User M1288: ristrutturato l'ordine delle
                  sezioni. Prima i portali (pubblicazione), poi i materiali
                  singoli (uso quotidiano), per ULTIMO il pacchetto cliente
                  (modalità "consegna" — capita meno spesso, non deve essere
                  il primo tile visibile). */}
              <Section
                eyebrow="Per i portali"
                title="Pubblica online"
                sub="Per Immobiliare.it scarichi il pacchetto pronto da caricare. Il Feed XML automatico per Immobiliare e Idealista è in arrivo."
              >
                {enhancedCount > 0 ? (
                  <Tile
                    icon={Archive}
                    primary
                    label="Pacchetto Immobiliare.it"
                    sub={portalExcludedCount > 0
                      ? `${portalIncludedCount} foto pronte · ${portalExcludedCount} escluse · scegli pianta`
                      : `${enhancedCount} foto · scegli versione pianta`}
                    action={() => setPortalPack({ portal: "immobiliare" })}
                    testId="file-tile-immobiliare-pack"
                  />
                ) : (
                  <Tile
                    icon={Archive}
                    label="Pacchetto Immobiliare.it"
                    sub="Migliora prima le foto"
                    action={() => setTab("foto")}
                    needsSetup
                    testId="file-tile-immobiliare-pack"
                  />
                )}
                <Tile
                  icon={Export}
                  label="Feed XML (in arrivo)"
                  sub="In sviluppo · presto disponibile per Immobiliare.it + Idealista"
                  action={() => toast.info("Feed XML automatico in arrivo · stiamo costruendo i formati specifici per Immobiliare.it e Idealista")}
                  needsSetup
                  testId="file-tile-xml"
                />
              </Section>

              {/* ═══ 2. Materiali singoli ════════════════════════════ */}
              <Section
                eyebrow="Materiali singoli"
                title="Da scaricare separatamente"
                sub="Quando ti serve solo un elemento: le foto, la pianta, il testo descrizione, il link pubblico, il QR code, o il kit completo per archivio interno."
              >
                <Tile
                  icon={Archive}
                  label="Foto migliorate · ZIP"
                  sub={enhancedCount > 0
                    ? `${enhancedCount} foto migliorate + le coppie prima/dopo`
                    : "Migliora prima le foto"}
                  action={enhancedCount > 0 ? () => onDownloadZip("enhanced") : () => setTab("foto")}
                  needsSetup={enhancedCount === 0}
                  testId="file-tile-photos-zip"
                />
                <Tile
                  icon={House}
                  label="Pianta arredata"
                  sub={hasPlan ? "Anteprima · alta risoluzione" : "Genera la pianta arredata"}
                  action={hasPlan
                    ? () => setPreview({
                        kind: "image",
                        src: authedFileUrl(floorPlans[0].furnished_plan_path),
                        title: "Pianta arredata",
                        filename: `pronto-pianta-${id}.jpg`,
                      })
                    : () => setTab("pianta")}
                  needsSetup={!hasPlan}
                  testId="file-tile-plan"
                />
                <Tile
                  icon={FilePdf}
                  label="PDF Pianta stampabile"
                  sub={hasPlan ? "A4 con pianta + dati immobile" : "Crea prima la pianta arredata"}
                  action={hasPlan ? async () => {
                    try {
                      const token = Cookies.get("vista_jwt");
                      toast.info("Genero il PDF…");
                      const r = await fetch(`${API}/properties/${id}/floorplan/pdf`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `pronto-pianta-${id.slice(0, 8)}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1500);
                      toast.success("PDF pianta scaricato");
                    } catch (e) {
                      toast.error("Download fallito: " + (e.message || ""));
                    }
                  } : () => setTab("pianta")}
                  needsSetup={!hasPlan}
                  testId="file-tile-plan-pdf"
                />
                <Tile
                  icon={FileText}
                  label="Testo descrizione"
                  sub={hasDescription
                    ? `${(property.ai_description || "").length} car. · vedi tutto`
                    : "Genera prima nel tab Annuncio"}
                  action={hasDescription
                    ? () => setPreview({
                        kind: "text",
                        src: property.ai_description,
                        title: "Testo descrizione",
                        subtitle: property.title || "",
                        filename: `${(property.title || "annuncio").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}.txt`,
                        onEdit: () => { setPreview(null); setTab("descrizione"); },
                      })
                    : () => setTab("descrizione")}
                  needsSetup={!hasDescription}
                  testId="file-tile-description"
                />
                <Tile
                  icon={ShareNetwork}
                  label="Link pubblico"
                  sub={hasListing ? "Copia il link annuncio" : "Serve la pagina pubblica · clicca per vedere"}
                  action={hasListing ? async () => {
                    const r = await copyToClipboard(shareUrl, { shareTitle: "Link annuncio" });
                    if (r === "clipboard") toast.success("Link copiato");
                    else if (r === "manual") toast.error("Copia non riuscita. Riprova.");
                  } : () => setSetupGate({
                    title: "Link pubblico",
                    subtitle: "URL univoco dell'annuncio da condividere su WhatsApp/email",
                    message: "Il link punta alla pagina pubblica dell'annuncio. Per generarlo devi prima renderlo online (gratuito · reversibile).",
                    testId: "public-link",
                  })}
                  needsSetup={!hasListing}
                  testId="file-tile-public-link"
                />
                <Tile
                  icon={QrCode}
                  label="QR code annuncio"
                  sub={hasListing ? "PNG ad alta risoluzione" : "Serve la pagina pubblica · clicca per vedere"}
                  action={hasListing ? downloadQr : () => setSetupGate({
                    title: "QR code annuncio",
                    subtitle: "PNG ad alta risoluzione per vetrine, volantini, biglietti",
                    message: "Il QR porta alla pagina pubblica dell'annuncio. Per generarlo devi prima renderlo online (gratuito · reversibile).",
                    testId: "qr",
                  })}
                  needsSetup={!hasListing}
                  testId="file-tile-qr"
                />
                <Tile
                  icon={Archive}
                  label="Kit completo · ZIP"
                  sub={kitReady
                    ? "Tutto in un file (per il tuo archivio): foto + descrizione + caption + email proprietario + prima/dopo"
                    : kitWaitingLabel}
                  action={kitReady
                    ? () => downloadKit("full")
                    : () => toast.info("Pacchetto non ancora pronto", {
                        description: kitWaitingLabel,
                        duration: 5000,
                      })}
                  needsSetup={!kitReady}
                  testId="file-tile-kit-zip"
                />
              </Section>

              {/* ═══ 3. Per il cliente (in fondo come da richiesta utente) ═══
                  iter v28 r378 — User M1288: «mettimelo non come primo, ma
                  più in basso». Pacchetto cliente staccato dagli altri tile
                  perché è un'azione di "consegna" (capita meno frequente di
                  scaricare la pianta o il PDF). + rimosso "Condividi su
                  WhatsApp" (utente diceva «cos'è che condivido?»): la
                  condivisione WA del pacchetto è ora SOLO via il modal
                  pre-compilato post-download del Pacchetto cliente. */}
              <Section
                eyebrow="Per il cliente"
                title="Cosa mandi al venditore"
                sub="Solo il materiale finito da consegnare al proprietario: le foto sistemate, la pianta arredata, le coppie prima/dopo e il link alla vetrina. Niente testi interni."
              >
                <Tile
                  icon={Archive}
                  primary={kitReady}
                  label="Pacchetto cliente · ZIP"
                  sub={kitReady
                    ? "Foto + pianta + prima/dopo + link vetrina. Niente caption social, niente bozze."
                    : kitWaitingLabel}
                  action={kitReady ? async () => {
                    // iter v28 r377 — Apriamo prima la preview (foto + link),
                    // poi al click "Scarica e procedi" parte il download e
                    // poi il modal WhatsApp pre-compilato.
                    try {
                      const token = Cookies.get("vista_jwt");
                      toast.info("Preparo l'anteprima…");
                      const r = await fetch(`${API}/properties/${id}/kit/preview?audience=client`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                      const data = await r.json();
                      setClientPackPreview(data);
                    } catch (e) {
                      console.warn("Preview kit fallita, vado al download diretto", e);
                      downloadKit("client");
                    }
                  } : () => toast.info("Pacchetto non ancora pronto", {
                    description: kitWaitingLabel,
                    duration: 5000,
                  })}
                  needsSetup={!kitReady}
                  testId="file-tile-client-pack"
                />
              </Section>

              {/* iter v28 r377 (future #1) — Modal preview Pacchetto cliente.
                  Mostra mini-galleria foto migliorate + caption + link vetrina
                  prima del download. Conferma → download → WA modal.
                  NB: renderizzato QUI dentro l'IIFE per avere accesso alla
                  closure `downloadKit` locale. */}
              {clientPackPreview && (
                <ClientPackPreviewModal
                  preview={clientPackPreview}
                  authedFileUrl={authedFileUrl}
                  onClose={() => setClientPackPreview(null)}
                  onConfirm={() => {
                    setClientPackPreview(null);
                    downloadKit("client");
                  }}
                />
              )}
            </>
          );
        })()}

        {/* Anteprima link pubblico se attivo */}
        {listing?.exists && (
          <div className="mt-8 bg-[#F5F4F0] border border-[#E5E7E0] rounded-xl p-4 max-w-5xl" data-testid="file-public-link-box">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-1.5">
              Pagina pubblica
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="flex-1 min-w-0 text-[12.5px] truncate text-[#1C1C1A]">{publicListingUrl}</code>
              <a
                href={publicListingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[12px] text-[#7C8B6E] hover:underline font-semibold"
                data-testid="file-public-link-preview"
              >
                <Eye size={13} weight="fill" /> Anteprima
              </a>
            </div>
          </div>
        )}

        {/* Universal preview modal (PDF, image, testo) */}
        <AssetPreviewModal
          open={!!preview}
          onClose={() => setPreview(null)}
          kind={preview?.kind}
          src={preview?.src}
          filename={preview?.filename}
          title={preview?.title}
          subtitle={preview?.subtitle}
          onEdit={preview?.onEdit}
        />

        {/* iter 296: Setup gate modal — quando il materiale richiede la
            pagina pubblica attiva. Mostra anteprima + spiegazione + CTA
            "Rendi annuncio online" invece di redirect silenzioso. */}
        <MaterialPreviewModal
          open={!!setupGate}
          onClose={() => setSetupGate(null)}
          title={setupGate?.title}
          subtitle={setupGate?.subtitle}
          needsSetup
          setupLabel="Rendi annuncio online"
          onSetup={() => setTab("condivisione")}
          testId={setupGate?.testId || "material"}
        />

        {/* iter v28 r295 — Modal "Analizza annuncio" — score 0-100 + 5 punti */}
        {showAnalysis && (
          <ListingAnalysisModal
            propertyId={id}
            onClose={() => setShowAnalysis(false)}
          />
        )}

        {/* iter v28 r348 — Modal "Cosa pubblicare" pre-download pacchetto portale */}
        {portalPack && (
          <PortalPackModal
            propertyId={id}
            portal={portalPack.portal}
            includedCount={realPhotos.filter((p) => !p.is_floor_plan && p.enhanced_path && !p.portal_excluded).length}
            excludedCount={realPhotos.filter((p) => !p.is_floor_plan && p.portal_excluded).length}
            hasFurnishedPlan={!!(floorPlans[0]?.furnished_plan_path)}
            hasOriginalPlan={!!(floorPlans[0]?.original_path)}
            onClose={() => setPortalPack(null)}
            onGoToPhotos={() => { setPortalPack(null); setTab("foto"); }}
          />
        )}

        {/* iter v28 r377 — Mini-modal "Ora mandalo al venditore" che compare
            DOPO il download del Pacchetto cliente. Mostra il messaggio
            WhatsApp pre-compilato (saluti formali + titolo immobile + link
            vetrina) e apre wa.me con un click. Risolve il "ok ho il zip,
            adesso come glielo mando?". */}
        {clientPackReady && (
          <ClientPackWhatsAppModal
            propertyTitle={clientPackReady.propertyTitle}
            vetrinaUrl={clientPackReady.vetrinaUrl}
            onClose={() => setClientPackReady(null)}
          />
        )}

        {/* iter v28 r178 — Studio Visivo · soft upsell partner.
            Per gli annunci premium dove serve rendering professionale fatto
            a mano. Volutamente in fondo, non in cima: prima i materiali
            standard, poi l'extra "su richiesta". */}
        <div id="studio-visivo" className="mt-10 pt-8 border-t border-[#E8E5DE]">
          <StudioVisivoCard
            propertyId={id}
            propertyTitle={property?.title}
            testIdPrefix="materiali-studio-visivo"
          />
        </div>
      </div>
  );
}


/**
 * ClientPackPreviewModal — iter v28 r377 (future #1)
 *
 * Anteprima del "Pacchetto cliente" PRIMA del download. Mostra:
 *   - Mini galleria 2x3 delle foto migliorate (max 6)
 *   - Caption che finirà nel zip (whitespace-pre-wrap)
 *   - Link vetrina pubblica
 *   - Conteggio totale foto
 * + CTA "Scarica e procedi" → chiude la preview, scarica lo zip, e apre
 * automaticamente il ClientPackWhatsAppModal (chain coerente: vedi,
 * scarichi, mandi).
 */
function ClientPackPreviewModal({ preview, onClose, onConfirm, authedFileUrl }) {
  const photos = preview?.preview_photos || [];
  const total = preview?.photo_count || 0;
  const photosToShow = photos.slice(0, 6);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center sm:justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="client-pack-preview-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#FAF8F3] w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#1C1C1A]/10 bg-white shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">
                <Archive size={12} weight="fill" /> Anteprima · Pacchetto cliente
              </div>
              <h2 className="text-[19px] sm:text-[21px] font-serif text-[#1C1C1A] mt-1 leading-tight">
                {preview?.title || "Annuncio"}
              </h2>
              <p className="text-[12.5px] text-[#6B6A65] mt-1.5 leading-snug">
                Cosa stai per inviare al venditore. Niente bozze interne, niente descrizione
                lunga: solo le foto sistemate, la caption pronta e il link alla vetrina.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-9 h-9 rounded-full hover:bg-[#1C1C1A]/5 inline-flex items-center justify-center text-[#1C1C1A]"
              aria-label="Chiudi"
              data-testid="client-pack-preview-close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-5 overflow-y-auto flex-1">
          {/* Foto migliorate (mini galleria) */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">
              Foto migliorate <span className="text-[#7C8B6E]">· {total} totali</span>
            </div>
            {photosToShow.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5">
                {photosToShow.map((p, i) => (
                  <div
                    key={p.id || i}
                    className="relative aspect-[4/3] bg-[#1C1C1A]/5 rounded-md overflow-hidden"
                    data-testid={`client-pack-preview-photo-${i}`}
                  >
                    <img
                      src={authedFileUrl(p.path)}
                      alt={`Foto ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {p.is_floor_plan && (
                      <span className="absolute top-1 left-1 text-[9px] uppercase font-bold bg-white/90 text-[#1C1C1A] px-1.5 py-0.5 rounded">
                        Pianta
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-[#E5E7E0] rounded-md p-4 text-center text-[12.5px] text-[#9A968B]">
                Nessuna foto migliorata. Vai alla galleria per processarle prima
                di mandare il pacchetto al cliente.
              </div>
            )}
            {total > photosToShow.length && (
              <div className="text-[10.5px] text-[#9A968B] mt-1.5 text-center">
                + altre {total - photosToShow.length} foto nel pacchetto
              </div>
            )}
          </div>

          {/* Pianta + prima/dopo · pillole informative (no caption) */}
          {(preview?.has_floor_plan || preview?.before_after_count > 0) && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">
                Cosa altro c'è nel pacchetto
              </div>
              <div className="flex flex-wrap gap-2" data-testid="client-pack-preview-extras">
                {preview?.has_floor_plan && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E7E0] text-[11.5px] text-[#3F3D38]">
                    <House size={12} weight="bold" className="text-[#7C8B6E]" /> Pianta arredata
                  </span>
                )}
                {preview?.before_after_count > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E7E0] text-[11.5px] text-[#3F3D38]">
                    <ImageIcon size={12} weight="bold" className="text-[#7C8B6E]" /> {preview.before_after_count} coppie prima/dopo
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Link vetrina */}
          {preview?.vetrina_url && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-2">
                Link vetrina personale (link-vetrina.txt nel zip)
              </div>
              <a
                href={preview.vetrina_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white border border-[#E5E7E0] rounded-md px-3.5 py-2.5 text-[12px] text-[#7C8B6E] hover:text-[#1C1C1A] hover:border-[#1C1C1A] transition-colors truncate"
                data-testid="client-pack-preview-vetrina-link"
              >
                {preview.vetrina_url}
              </a>
            </div>
          )}
        </div>

        <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-[#1C1C1A]/10 bg-white shrink-0 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onClose}
            className="sm:flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white border border-[#E5E7E0] hover:border-[#1C1C1A] text-[#1C1C1A] text-[14px] font-semibold transition-colors"
            data-testid="client-pack-preview-cancel"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="sm:flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[14px] font-semibold transition-colors"
            data-testid="client-pack-preview-confirm"
          >
            <DownloadSimple size={15} weight="bold" /> Scarica e procedi
          </button>
        </div>
      </div>
    </div>
  );
}

/** Helper: costruisce URL autenticato per immagini dello storage interno
 *  (lo storage richiede `?auth=` per le risorse non-pubbliche). Estratto
 *  qui per non dipendere dal hook chiuso del componente principale. */
function authedFileUrlFor(path) {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  const token = Cookies.get("vista_jwt");
  const sep = path.includes("?") ? "&" : "?";
  return `${API}/files/${path.replace(/^\/+/, "")}${token ? `${sep}auth=${encodeURIComponent(token)}` : ""}`;
}


/**
 * ClientPackWhatsAppModal — iter v28 r377
 *
 * Mini-modal che compare DOPO il download del "Pacchetto cliente · ZIP".
 * Mostra un messaggio WhatsApp pre-compilato (saluti formali italiani +
 * titolo immobile + link vetrina online) e apre `wa.me/?text=…` al click.
 *
 * Pattern: l'agente scarica lo zip, lo allega manualmente in WhatsApp,
 * questo bottone gli da' il TESTO già scritto in modo professionale.
 */
function ClientPackWhatsAppModal({ propertyTitle, vetrinaUrl, onClose }) {
  const linkLine = vetrinaUrl
    ? `\n\nLe lascio il link diretto alla sua vetrina personale: ${vetrinaUrl}\n\nIn allegato trova il pacchetto completo del materiale (foto, pianta arredata, prima/dopo).`
    : "\n\nIn allegato trova il pacchetto completo del materiale (foto, pianta arredata, prima/dopo).";
  const message =
    `Buongiorno,\n\nle invio il materiale dedicato al Suo immobile "${propertyTitle}".${linkLine}\n\nResto a disposizione per qualunque chiarimento.\n\nUn cordiale saluto.`;
  const [copied, setCopied] = React.useState(false);

  const openWa = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
  };
  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Messaggio copiato");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center sm:justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="client-pack-wa-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#FAF8F3] w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#1C1C1A]/10 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#25D366]">
                <WhatsappLogo size={12} weight="fill" /> Pacchetto pronto
              </div>
              <h2 className="text-[19px] sm:text-[21px] font-serif text-[#1C1C1A] mt-1 leading-tight">
                Ora mandalo al venditore
              </h2>
              <p className="text-[12.5px] text-[#6B6A65] mt-1.5 leading-snug">
                Il messaggio è già scritto in modo professionale. Tu apri WhatsApp,
                scegli il contatto, allega il file zip che hai appena scaricato.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-9 h-9 rounded-full hover:bg-[#1C1C1A]/5 inline-flex items-center justify-center text-[#1C1C1A]"
              aria-label="Chiudi"
              data-testid="client-pack-wa-close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-4">
          {/* Anteprima messaggio in stile chat */}
          <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-sm px-4 py-3 text-[13.5px] text-[#1C1C1A] leading-[1.55] whitespace-pre-wrap shadow-sm" data-testid="client-pack-wa-message">
            {message}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={openWa}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#25D366] hover:bg-[#1FB955] text-white text-[14px] font-semibold transition-colors"
              data-testid="client-pack-wa-open"
            >
              <WhatsappLogo size={16} weight="fill" /> Apri WhatsApp
            </button>
            <button
              type="button"
              onClick={copyMsg}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white border border-[#E5E7E0] hover:border-[#1C1C1A] text-[#1C1C1A] text-[14px] font-semibold transition-colors"
              data-testid="client-pack-wa-copy"
            >
              <Copy size={15} weight="bold" /> {copied ? "Copiato" : "Copia messaggio"}
            </button>
          </div>

          <div className="text-[10.5px] text-[#9A968B] leading-snug">
            Suggerimento: il file zip si trova nei tuoi Download. Aprilo in WhatsApp
            tramite l'icona della graffetta dopo aver iniziato la chat col venditore.
          </div>
        </div>
      </div>
    </div>
  );
}
